import React, { FunctionComponent, memo, useEffect, useState } from "react";
import { Button, Input, TextFieldProps } from "@mui/material";
import styles from "./TextFieldWithButtons.module.scss";
import { Utils } from "../../../../utils/utils";
import { CircleButton, TextFieldCustom } from "../index";

type InputWithEvalProps = {
    isEval: boolean;
    inputCords?: any;
    objValue: number | { value: number };
    setObjValue: (value: any) => void;
    maxAfterBlur?: number;
    MIN_MAX_INPUT?: any;
    inputKey?: string;
    min: number;
    max: number;
    step?: number;
    diffScale?: number;
    disabled?: boolean;
    hotChangeValue?: boolean;
    onFocus?: () => void;
    autoFocus?: boolean;
    hideButtons?: boolean;
} & TextFieldProps;

const TextFieldWithButtons: FunctionComponent<InputWithEvalProps> = memo(
    ({
        isEval = false,
        inputCords,
        objValue,
        setObjValue,
        MIN_MAX_INPUT,
        maxAfterBlur,
        inputKey = "",
        min,
        max,
        step = 50,
        diffScale = 1,
        disabled,
        hotChangeValue = false,
        onFocus,
        autoFocus = true,
        hideButtons,
        ...props
    }) => {

        const [inputValue, setInputValue] = useState<string | number>(
            typeof objValue === "object" ? objValue.value : objValue
        );
        const [isInputButtonsDisabled, setIsInputButtonsDisabled] = useState(false);

        const minInputValue = min || MIN_MAX_INPUT?.[inputKey]?.min || 0;
        const maxInputValue = max || MIN_MAX_INPUT?.[inputKey]?.max || 0;

        const stepInputValue = step
            ? step
            : +inputValue - 60 < minInputValue || +inputValue + 60 > maxInputValue
                ? 1
                : 50;

        useEffect(() => {
            const inputValue = typeof objValue === "object" ? objValue.value : objValue;
            setInputValue(inputValue);
        }, [objValue]);

        const setRequiredInputValue = (inputValue: number) => {
            let requiredInputValue = inputValue;
            if (typeof +inputValue === "number") {
                if (requiredInputValue > maxInputValue) {
                    requiredInputValue = maxInputValue;
                }
                if (requiredInputValue <= minInputValue) {
                    requiredInputValue = minInputValue;
                }
            }
            return requiredInputValue;
        };

        const onBlurInput = (tempInputValue: string | number) => {
            let value = tempInputValue || inputValue;
            if (isEval) {
                value = Utils.removeFirstNullFromNumbers(`${value}`);
                value = +eval(value);
            }
            value = inputValue === "" ? 0 : value;
            let requiredInputValue = setRequiredInputValue(+value);
            if (typeof objValue === "object") {
                setObjValue((prev: any) => ({
                    ...prev,
                    value: requiredInputValue,
                }));
            } else {
                if (objValue !== value) {
                    setObjValue(requiredInputValue);
                }
            }
            if (maxAfterBlur && requiredInputValue > maxAfterBlur) {
                requiredInputValue = maxAfterBlur
            }
            setInputValue(`${requiredInputValue}`);
        };

        const buttonUpHandler = (
            e:
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLDivElement>
                | React.WheelEvent<HTMLDivElement>,
            onWheel = false
        ) => {
            if (isInputButtonsDisabled) {
                return;
            }
            if (typeof +inputValue === "number") {
                let result = +inputValue + stepInputValue;
                if (result > maxInputValue) {
                    result = maxInputValue;
                }
                setInputValue(result);
                if (hotChangeValue && !onWheel) {
                    onBlurInput(result);
                }
            }
        };

        const buttonDownHandler = (
            e:
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLDivElement>
                | React.WheelEvent<HTMLDivElement>,
            onWheel = false
        ) => {
            if (isInputButtonsDisabled) {
                return;
            }
            if (typeof +inputValue === "number") {
                let result = +inputValue - stepInputValue;
                if (result <= minInputValue) {
                    result = minInputValue;
                }
                setInputValue(result);
                if (hotChangeValue && !onWheel) {
                    onBlurInput(result);
                }
            }
        };
        return (
            <div
                onClick={(e) => e.stopPropagation()}
                style={props.style}

            // style={{
            //     position: inputCords ? "absolute" : "relative",
            //     background: "white",
            //     borderRadius: "5px",
            //     height: inputCords ? "5px" : "100%",
            //     transform: "translateX(-50%)",
            //     top: inputCords?.top,
            //     left: inputCords?.left,
            // }}
            >
                <div className={styles.box}>
                    {!isInputButtonsDisabled && !hideButtons && (
                        <CircleButton
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={buttonDownHandler}
                            color={"inherit"}
                            diameter={32}
                            variant={"contained"}
                            className={styles.btn}
                        >
                            <span>-</span>
                        </CircleButton>
                    )}
                    <TextFieldCustom
                        disabled={disabled}
                        autoFocus={autoFocus}
                        autoComplete={"off"}
                        onFocus={(e) => {
                            e.stopPropagation();
                            onFocus && onFocus(e);
                        }}
                        className={!hideButtons ? styles.textField : undefined}
                        value={inputValue}
                        style={props.style}
                        onChange={(event) => {
                            event.stopPropagation();
                            const inputValue = event.target.value.replace(/[^-()\d/*+.]/g, "");
                            if (Number.isNaN(+inputValue)) {
                                setIsInputButtonsDisabled(true);
                            } else {
                                setIsInputButtonsDisabled(false);
                            }
                            setInputValue(inputValue);
                        }}
                        onWheel={(e) => {
                            e.stopPropagation();

                            if (e.deltaY < 0) {
                                buttonUpHandler(e, true);
                            }
                            if (e.deltaY > 0) {
                                buttonDownHandler(e, true);
                            }
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
                            e.stopPropagation();

                            if (e.keyCode === 13) {
                                // @ts-ignore
                                e.target.blur();
                            }
                            if (e.keyCode === 40) {
                                buttonDownHandler(e);
                            }
                            if (e.keyCode === 38) {
                                buttonUpHandler(e);
                            }
                        }}
                        onBlur={(e) => {
                            e.stopPropagation();
                            setIsInputButtonsDisabled(false);
                            onBlurInput("");
                        }}
                        size="small"
                        inputProps={{
                            ...props.inputProps,
                            className: styles.input,
                            type: "text",
                        }}
                    />
                    {!isInputButtonsDisabled && !hideButtons && (
                        <CircleButton
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={buttonUpHandler}
                            color={"inherit"}
                            diameter={32}
                            variant={"contained"}
                            className={styles.btn}
                        >
                            <span>+</span>
                        </CircleButton>
                    )}
                </div>
            </div>
        );
    }
);

export default TextFieldWithButtons;
